import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ArrowLeft, ShieldCheck, Sparkle, Key } from '@phosphor-icons/react';
import Header from '@/components/Header';
import PremiumFooter from '@/components/PremiumFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRO_TOKEN_KEY = 'nova_pro_token';

const WellnessProLoginPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(searchParams.get('token') || localStorage.getItem(PRO_TOKEN_KEY) || '');
  const [loading, setLoading] = useState(false);

  const attemptLogin = async (accessToken) => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/wellness/pro/login`, { access_token: accessToken });
      localStorage.setItem(PRO_TOKEN_KEY, accessToken);
      toast.success(`Welcome, ${res.data.professional.full_name}`);
      navigate('/wellness/pro/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid token');
      localStorage.removeItem(PRO_TOKEN_KEY);
    } finally {
      setLoading(false);
    }
  };

  // Auto-login if token came from URL or was stored
  useEffect(() => {
    const urlToken = searchParams.get('token');
    const storedToken = localStorage.getItem(PRO_TOKEN_KEY);
    if (urlToken) {
      attemptLogin(urlToken);
    } else if (storedToken) {
      attemptLogin(storedToken);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!token.trim()) {
      toast.error('Please paste your access token');
      return;
    }
    attemptLogin(token.trim());
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="pro-login-page">
      <Header user={user} />
      <section className="max-w-lg mx-auto px-6 py-16">
        <button onClick={() => navigate('/wellness')} className="text-sm text-stone-500 hover:text-[#1A4331] mb-6 inline-flex items-center gap-1">
          <ArrowLeft size={14} weight="bold" /> Back to Wellness
        </button>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl p-8 border border-stone-100 shadow-xl shadow-stone-200/40">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1A4331] to-[#2A5A44] flex items-center justify-center mb-5">
            <Key size={26} weight="duotone" className="text-[#34D399]" />
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#C05C3D] mb-2">Professional Login</p>
          <h1 className="text-3xl font-semibold text-[#1C1917] font-heading tracking-tight mb-2">Access your dashboard</h1>
          <p className="text-stone-600 text-sm leading-relaxed mb-6">Paste the access token you received when you joined Nova Wellness.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-1.5">Access Token</label>
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste your 40-character token"
                className="w-full bg-white border border-stone-200 focus:border-[#1A4331] focus:ring-4 focus:ring-[#1A4331]/10 rounded-2xl px-4 py-3 outline-none transition-all text-[14px] text-[#1C1917] placeholder:text-stone-400 font-mono"
                required
                data-testid="pro-login-token-input"
              />
            </div>
            <button type="submit" disabled={loading} className="w-full bg-[#1A4331] text-white py-3 rounded-full font-semibold hover:bg-[#2A5A44] transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-2" data-testid="pro-login-submit">
              {loading ? 'Verifying...' : 'Log In'}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-stone-100 space-y-3 text-xs text-stone-500">
            <div className="flex gap-2"><ShieldCheck size={14} weight="fill" className="text-[#1A4331] flex-shrink-0 mt-0.5" /><p>Your access token is unique and private. Treat it like a password.</p></div>
            <div className="flex gap-2"><Sparkle size={14} weight="fill" className="text-[#C05C3D] flex-shrink-0 mt-0.5" /><p>Don&apos;t have one yet? <button onClick={() => navigate('/wellness/join')} className="text-[#1A4331] font-semibold hover:underline">Join as a Professional</button></p></div>
          </div>
        </motion.div>
      </section>
      <PremiumFooter />
    </div>
  );
};

export default WellnessProLoginPage;
export { PRO_TOKEN_KEY };
